import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGoogleClientId } from "src/store/common"
import { loadGoogleIdentity } from "src/utils/googleIdentity"

import "./GoogleSignInButton.css"

/* The Google button, drawn by Google.

   `renderButton` paints into a div we own; the callback receives `{ credential }`, which is the ID
   token the backend verifies. We never see the password, the profile, or an OAuth code — one signed
   string is the whole handoff.

   Renders nothing when the server publishes no client ID (`GET /game-config/`). A button that
   cannot work is worse than an absent one: it fails at the click, after the player has committed to
   signing in that way. */
/* How wide to ask Google to draw its button.
   
   260 matches the buttons on the sign-in form above it, which is why it was pinned there — but a
   pinned width overflows anywhere narrower, and the profile row on a 360px phone is exactly that.
   So: 260 where it fits, the room available where it does not, and never below Google's own
   minimum (it accepts 200–400 and refuses to draw outside that).
   
   The container itself cannot be measured — it is `align-self: center`, so it shrinks to whatever
   Google drew and reports the width it was given. The parent is the one that knows. */
const PREFERRED_BUTTON_PX = 260
const MIN_BUTTON_PX = 200
const MAX_BUTTON_PX = 400

export const buttonWidth = (available) =>
  Math.round(Math.min(MAX_BUTTON_PX, Math.max(MIN_BUTTON_PX, Math.min(PREFERRED_BUTTON_PX, available || PREFERRED_BUTTON_PX))))

/* Long enough that a slow frame is not mistaken for a failure, short enough that nobody sits
   looking at a gap. Google's own button appears in one frame when it appears at all. */
const GOOGLE_BUTTON_GRACE_MS = 1200

const GoogleSignInButton = ({ onCredential, onError, text = "signin_with", divider = true }) => {
  const { t, i18n } = useTranslation()
  const clientId = useSelector(selectGoogleClientId)
  const container = useRef(null)
  const [unavailable, setUnavailable] = useState(false)

  // The callbacks are held in a ref so that a parent re-rendering with a new closure does not
  // re-run the effect and make Google paint a second button into the same div.
  const handlers = useRef({ onCredential, onError })
  handlers.current = { onCredential, onError }

  useEffect(() => {
    if (!clientId) return undefined

    let cancelled = false
    loadGoogleIdentity()
      .then((googleId) => {
        if (cancelled || !container.current) return
        googleId.initialize({
          client_id: clientId,
          callback: (response) => handlers.current.onCredential?.(response.credential),
        })
        /* A frame first, and it is load-bearing. The button's width has to be measured, and this
           effect runs while the modal that contains it is still being laid out: `clientWidth` was
           0 at this point, so every measurement fell back to the default and a small phone got a
           260px button in a 199px column. One frame later the box exists. */
        requestAnimationFrame(() => {
          if (cancelled || !container.current) return
          googleId.renderButton(container.current, {
            theme: "filled_black",
            size: "large",
            shape: "rectangular",
            logo_alignment: "center",
            text,
            width: buttonWidth(container.current.parentElement?.clientWidth),
            // Google draws its own words, so it has to be told which ones. Without this a Spanish
            // player reads a whole screen in Spanish and then an English button on the end of it.
            locale: i18n.language,
          })
          /* And then check that it drew one. `renderButton` paints into the div and reports
             nothing back, so a client ID Google does not recognise — the ordinary shape of a
             misconfiguration, since the ID is set on the server and published through
             /game-config/ — leaves an empty box under the "or", which reads as a button that has
             not finished loading and never will. A rejected script already says so; this is the
             same sentence for the case where the script arrived and the button did not. */
          setTimeout(() => {
            if (cancelled || !container.current) return
            if (container.current.childElementCount === 0) setUnavailable(true)
          }, GOOGLE_BUTTON_GRACE_MS)
        })
      })
      .catch(() => {
        if (cancelled) return
        // Blocked script, no network. Say so rather than leaving a gap where a button was: the
        // player still has the form above it and now knows why the other way is missing.
        setUnavailable(true)
      })

    return () => {
      cancelled = true
    }
  }, [clientId, text, i18n.language])

  if (!clientId) return null

  /* The divider belongs to the button, not to the form above it: with no client ID neither
     renders, so a stack without Google sign-in doesn't end in a stray "or" line.

     It is off in the profile pop-up, though, where there is no form above it and nothing for
     Google to be an alternative *to* — an "or" between the language switcher and a Google button
     reads as a separator somebody forgot to finish. */
  return (
    <div className="google-signin">
      {divider ? <p className="auth-divider">{t("auth.divider")}</p> : null}
      {unavailable ? (
        <p className="p-mini google-signin-unavailable">{t("auth.googleUnavailable")}</p>
      ) : (
        <div ref={container} className="google-signin-target" />
      )}
    </div>
  )
}

export default GoogleSignInButton
